import { plural } from "@lingui/core/macro";
import { observer } from "mobx-react-lite";
import React, { useEffect, useRef } from "react";

import { cn } from "@/utils/shadcn-ui";

import { useFormFieldId } from "../form/form-field-id-context";
import { BaseInput } from "./base-input";
import { BaseTextarea } from "./base-textarea";
import { FieldProps, useField, useModelBinding } from "./utils";
import { ValidationTooltip } from "./validation-tooltip";

interface Props<M extends object> extends FieldProps<M, string> {
  type?: "text" | "email" | "password" | "number";
  width?: number;
  multiline?: boolean | "autosize";
  required?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
  placeholder?: string;
  className?: string;
  rows?: number;
  maxLength?: number;
  /** Counted without surrounding whitespace, so spaces alone never pass.
   *  An empty value is left to `required`. */
  minLength?: number;
  /** For inputs with no visible label, which would otherwise be unnamed. A
   *  placeholder does not name a field: it is gone as soon as there is
   *  something in it to read back. */
  ariaLabel?: string;
}

export const TextInput = observer(function TextInput<M extends object>({
  type = "text",
  width,
  multiline,
  required,
  disabled,
  autoFocus,
  placeholder,
  className,
  rows,
  maxLength,
  minLength,
  ariaLabel,
  ...restProps
}: Props<M>) {
  const formFieldId = useFormFieldId();
  const elementRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const { isInvalid, tooltip } = useField(elementRef);

  const { value: bindingValue, onChange } = useModelBinding(restProps);
  const value = bindingValue ?? "";

  // Native `minlength` counts spaces and only checks text the user typed, so
  // the trimmed length is enforced as a custom validity instead.
  useEffect(() => {
    // Some callers bind numbers, despite the `string` type.
    const length = String(value).trim().length;
    elementRef.current?.setCustomValidity(
      minLength && value && length < minLength
        ? plural(minLength, {
            one: "Enter at least # character.",
            other: "Enter at least # characters.",
          })
        : "",
    );
  }, [value, minLength]);

  return (
    <ValidationTooltip model={tooltip}>
      {!multiline ? (
        <BaseInput
          id={formFieldId}
          type={type}
          value={value}
          onChange={(event) => {
            onChange(event.target.value);
          }}
          style={{ width: width ? `${width}px` : undefined, maxWidth: "100%" }}
          required={required}
          ref={elementRef as React.RefObject<HTMLInputElement>}
          className={className}
          aria-label={ariaLabel}
          aria-invalid={isInvalid}
          disabled={disabled}
          autoFocus={autoFocus}
          placeholder={placeholder}
          maxLength={maxLength}
        />
      ) : (
        <BaseTextarea
          id={formFieldId}
          autosize={multiline === "autosize"}
          value={value}
          onChange={(event) => {
            onChange(event.target.value);
          }}
          style={{
            width: width ? `${width}px` : undefined,
            maxWidth: "100%",
            minHeight: rows ? `${rows * 1.5 + 0.75}em` : undefined,
          }}
          required={required}
          ref={elementRef as React.RefObject<HTMLTextAreaElement>}
          className={cn(
            // min(): a 200px floor that still yields to a narrower cell.
            "w-full min-w-[min(200px,100%)] max-h-50",
            multiline === "autosize" && "autosize",
            className,
          )}
          aria-label={ariaLabel}
          aria-invalid={isInvalid}
          rows={rows ?? (multiline === "autosize" ? 1 : undefined)}
          disabled={disabled}
          autoFocus={autoFocus}
          placeholder={placeholder}
          maxLength={maxLength}
        />
      )}
    </ValidationTooltip>
  );
});
